// @flow
import {observable, action, computed, makeObservable} from 'mobx';
import RootStore from 'mobx/RootStore';
import type {PubSubMessageType} from 'types';
import {toNotification} from './transforms';
import type {NotificationType} from './types';

export class AppNotificationStore {
  rootStore: RootStore;
  notifications: NotificationType<any>[] = [];

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      notifications: observable,
      addNotification: action,
      dismissNotification: action,
      defaultChannel: computed,
      calendarChannel: computed,
      count: computed,
    });
    this.rootStore = rootStore;
    this.notifications = [];
  }

  async init() {
    const {pubnubStore} = this.rootStore.stores;

    if (!pubnubStore.connected) {
      await pubnubStore.connect(); // Wait for PubNub to initialize
    }

    await this.getMessages();
  }

  handlePublishedMessage(
    message: PubSubMessageType,
    isRealtime: boolean = false,
    realtimeCallback?: () => void,
  ): void {
    const notification = toNotification(message);

    this.addNotification(notification);

    if (isRealtime && realtimeCallback) {
      realtimeCallback();
    }
  }

  async getMessages() {
    const {pubnubStore} = this.rootStore.stores;

    if (!pubnubStore.pubnub) {
      throw new Error('PubNub instance not available.');
    }

    const channels = [this.defaultChannel, this.calendarChannel];
    const end = '00000000000000001';

    pubnubStore.pubnub.fetchMessages({channels, end}, (status, response) => {
      if (status.error) {
        console.error('Error fetching messages:', status.error);
        return;
      }

      const messages = response
        ? Object.values(response.channels).flatMap((channel) => channel)
        : [];

      if (messages.length > 0) {
        pubnubStore.loadAllMessages(messages);
      } else {
        console.log('No messages found.');
      }
    });
  }

  addNotification(notification: NotificationType<any>) {
    this.notifications.unshift(notification);
  }

  dismissNotification(dismissedNotification: NotificationType<any>) {
    const updatedInbox = this.notifications.filter(
      (notification) =>
        notification.timetoken !== dismissedNotification.timetoken,
    );

    this.notifications.replace(updatedInbox);

    // $FlowFixMe
    this.rootStore.stores.pubnubStore.deleteMessage(
      dismissedNotification.channel,
      dismissedNotification.timetoken,
    );
  }

  get count(): number {
    return this.notifications.length;
  }

  get defaultChannel(): string {
    const {meStore} = this.rootStore.stores;
    // $FlowFixMe
    return `ch-inbox.${meStore.me.id}.notifications`;
  }

  get calendarChannel(): string {
    const {meStore} = this.rootStore.stores;
    // $FlowFixMe
    return `ch-inbox.${meStore.me.id}.calendar`;
  }

  get allNotificationChannels(): Array<string> {
    return [this.defaultChannel, this.calendarChannel];
  }
}
